/**
 * Panel de payload.
 *
 * Es el corazon de la herramienta: el estandar a igualar es el log actual de la
 * integracion SAP-WMS, que muestra el JSON enviado y la respuesta. Por eso el
 * JSON se muestra completo, monoespaciado, sin recortes y con un boton para
 * copiarlo entero.
 */
import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { prettyJson } from '../lib/format';

export interface JsonPanelProps {
  readonly title: string;
  readonly value: unknown;
  /** Texto cuando no hay nada que mostrar (p. ej. payload aun sin normalizar). */
  readonly emptyLabel?: string;
  /** Contenido extra en la cabecera, a la derecha del titulo. */
  readonly meta?: ReactNode;
}

export function JsonPanel({ title, value, emptyLabel, meta }: JsonPanelProps): ReactElement {
  const [copied, setCopied] = useState(false);
  const text = prettyJson(value);
  const isEmpty = text === '';

  const copy = (): void => {
    void navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // El portapapeles puede estar bloqueado (permisos, http): no es un fallo
        // del diagnostico, el JSON sigue visible y seleccionable en pantalla.
        setCopied(false);
      });
  };

  return (
    <section className="json">
      <header className="json__header">
        <span className="json__title">{title}</span>
        <span className="row">
          {meta}
          <button
            type="button"
            className="button button--sm button--ghost"
            onClick={copy}
            disabled={isEmpty}
          >
            {copied ? 'Copiado' : 'Copiar'}
          </button>
        </span>
      </header>
      {isEmpty ? (
        <p className="json__empty">{emptyLabel ?? 'Sin contenido.'}</p>
      ) : (
        <pre className="json__code">{text}</pre>
      )}
    </section>
  );
}
